/**
 * REST client for the backend. All methods send credentials (cookies) and parse JSON.
 * Throws on non-2xx so callers can `try/catch` and surface toasts.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const BASE = process.env.REACT_APP_BACKEND_URL;

const url = (p) => `${BASE}${p}`;

const handle = async (res) => {
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data?.detail) msg = data.detail;
    } catch (_) {}
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  return res.blob();
};

const request = (method, path, body, opts = {}) =>
  fetch(url(path), {
    method,
    credentials: 'include',
    headers: body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {},
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    ...opts,
  }).then(handle);

export const api = {
  // Auth
  me:             () => request('GET',  '/api/auth/me'),
  logout:         () => request('POST', '/api/auth/logout'),

  // Bootstrap & reset & migrate
  bootstrap: () => request('GET',  '/api/bootstrap'),
  resetAll:  () => request('POST', '/api/reset'),
  migrate:   (state) => request('POST', '/api/migrate', state),

  // Sources
  createSource: (body) => request('POST',   '/api/sources', body),
  updateSource: (id, body) => request('PATCH',  `/api/sources/${id}`, body),
  deleteSource: (id) => request('DELETE', `/api/sources/${id}`),

  // Dropdowns
  addDropdownValue:    (key, value)            => request('POST',   `/api/dropdowns/${encodeURIComponent(key)}`, { value }),
  removeDropdownValue: (key, value)            => request('DELETE', `/api/dropdowns/${encodeURIComponent(key)}/${encodeURIComponent(value)}`),
  renameDropdownValue: (key, oldValue, newValue) => request('PATCH', `/api/dropdowns/${encodeURIComponent(key)}`, { oldValue, newValue }),

  // Snapshots
  createSnapshot: (date, label) => request('POST', '/api/snapshots', { date, label }),
  updateSnapshot: (id, patch) => request('PATCH', `/api/snapshots/${id}`, patch),
  deleteSnapshot: (id) => request('DELETE', `/api/snapshots/${id}`),

  // Assets
  createAsset: (body) => request('POST',   '/api/assets', body),
  updateAsset: (id, body) => request('PATCH',  `/api/assets/${id}`, body),
  deleteAsset: (id) => request('DELETE', `/api/assets/${id}`),

  // Holdings
  saveHoldings: (snapId, srcId, rows) =>
    request('PUT', `/api/holdings/${snapId}/${srcId}`, { rows }),

  // Locks
  setSourceComplete: (snapId, srcId, complete) =>
    request('PUT', `/api/locks/${snapId}/source/${srcId}`, { complete }),
  setPeriodLock:     (snapId, locked) =>
    request('PUT', `/api/locks/${snapId}`, { locked }),

  // Backups
  addBackup:    (snapId, srcId, backup) => request('POST',   `/api/backups/${snapId}/${srcId}`, { backup }),
  removeBackup: (snapId, srcId, backupId) => request('DELETE', `/api/backups/${snapId}/${srcId}/${backupId}`),

  // FX
  // `srcId` optional — when provided, rate is scoped to that source only (overrides the snapshot default).
  setFxRate: (snapId, srcId, payload, source = 'manual') => {
    // Accepts {usdInrCost, usdInrMv} OR a single number for back-compat.
    const body = typeof payload === 'object'
      ? { ...payload, source }
      : { usdInr: Number(payload), source };
    const path = srcId ? `/api/fx/${snapId}/${srcId}` : `/api/fx/${snapId}`;
    return request('PUT', path, body);
  },
  clearSourceFxRate: (snapId, srcId) => request('DELETE', `/api/fx/${snapId}/${srcId}`),

  // Dashboard views
  createView: (body)        => request('POST',   '/api/views', body),
  updateView: (id, patch)   => request('PATCH',  `/api/views/${id}`, patch),
  deleteView: (id)          => request('DELETE', `/api/views/${id}`),

  // Excel
  excelTemplateUrl: (snapId, srcId) => url(`/api/excel/template/${snapId}/${srcId}`),
  excelAllSourcesUrl: (snapId, mode = 'consolidated') => url(`/api/excel/all/${snapId}?mode=${encodeURIComponent(mode)}`),
  excelDashboard: (body) => request('POST', '/api/excel/dashboard', body),
  excelPreview:     (snapId, srcId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return request('POST', `/api/excel/preview/${snapId}/${srcId}`, fd);
  },
  listUploads: () => request('GET', '/api/excel/uploads'),

  // AI Import — broker statement PDF → suggested rows (Claude Sonnet 4.5)
  // `instructions` is an optional one-off override; persisted instructions live on the source.
  aiImport: (snapId, srcId, file, instructions = null) => {
    const fd = new FormData();
    fd.append('file', file);
    if (instructions != null) fd.append('instructions', instructions);
    return request('POST', `/api/ai-import/${snapId}/${srcId}`, fd);
  },

  // Activity Log
  listActivity: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('GET', `/api/activity${qs ? '?' + qs : ''}`);
  },
  activityStats: () => request('GET', '/api/activity/stats'),

  // Admin
  adminMe: () => request('GET', '/api/admin/me'),
  adminOverview: () => request('GET', '/api/admin/overview'),
  adminUsers: () => request('GET', '/api/admin/users'),
  adminCreateShare: (body) => request('POST', '/api/admin/shares', body),
  adminDeleteShare: (id) => request('DELETE', `/api/admin/shares/${id}`),
  adminListAdmins: () => request('GET', '/api/admin/admins'),
  adminAddAdmin: (email) => request('POST', '/api/admin/admins', { email }),
  adminRemoveAdmin: (email) => request('DELETE', `/api/admin/admins/${encodeURIComponent(email)}`),
};

export default api;
